import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  isValidCode,
  thunkExecuteAndReturnResultOrShowError,
} from '../../services/base.service';
import {
  deleteExitAdjustmentFile,
  downloadExitAdjustmentFile,
  getAllExitAdjustmentFiles,
  getExitAdjustmentById,
  getExitAdjustmentFilesByRequest,
  getExitAdjustmentFilesByStudent,
  getExitAdjustmentFormViewModel,
  getExitAdjustmentLeas,
  getExitAdjustmentListItems,
  getExitAdjustmentSchoolsByLea,
  getExitAdjustmentSchoolYears,
  getExitAdjustmentStudentUpdate,
  getStudentByMSISId,
  uploadExitAdjustmentFile,
  deleteExitAdjustment,
} from '../../services/exitAdjustment.service';
import { ExitAdjustmentFileListItemModel } from '../../types/ExitAdjustmentFileListItemModel';
import { ExitAdjustmentFormViewModel } from '../../types/ExitAdjustmentFormViewModel';
import { ExitAdjustmentListItemModel } from '../../types/ExitAdjustmentListItemModel';
import { ExitAdjustmentModel } from '../../types/ExitAdjustmentModel';
import { SelectOption } from '../../types/SelectOption';

export const fetchExitAdjustmentListItems = createAsyncThunk(
  'exitAdjustment/fetchExitAdjustmentListItems',
  async (
    params: { schoolYear: string; lea: string; school: string; status: string },
    thunkAPI
  ) => {
    return await thunkExecuteAndReturnResultOrShowError<
      ExitAdjustmentListItemModel[]
    >(
      thunkAPI,
      getExitAdjustmentListItems(
        params.schoolYear,
        params.lea,
        params.school,
        params.status,
        { signal: thunkAPI.signal as AbortSignal }
      )
    );
  }
);

export const fetchExitAdjustmentFormViewModel = createAsyncThunk(
  'exitAdjustment/fetchExitAdjustmentFormViewModel',
  async (_, thunkAPI) => {
    return await thunkExecuteAndReturnResultOrShowError<ExitAdjustmentFormViewModel>(
      thunkAPI,
      getExitAdjustmentFormViewModel({
        signal: thunkAPI.signal as AbortSignal,
      })
    );
  }
);

export const fetchExitAdjustmentStudentUpdate = createAsyncThunk(
  'exitAdjustment/fetchExitAdjustmentStudentUpdate',
  async (schoolYear: string | undefined, thunkAPI) => {
    return await thunkExecuteAndReturnResultOrShowError<boolean>(
      thunkAPI,
      getExitAdjustmentStudentUpdate(schoolYear, {
        signal: thunkAPI.signal as AbortSignal,
      })
    );
  }
);

export const fetchExitAdjustmentSchoolYears = createAsyncThunk(
  'exitAdjustment/fetchExitAdjustmentSchoolYears',
  async (_, thunkAPI) => {
    return await thunkExecuteAndReturnResultOrShowError<SelectOption[]>(
      thunkAPI,
      getExitAdjustmentSchoolYears({
        signal: thunkAPI.signal as AbortSignal,
      })
    );
  }
);

export const fetchExitAdjustmentLeas = createAsyncThunk(
  'exitAdjustment/fetchExitAdjustmentLeas',
  async (_, thunkAPI) => {
    return await thunkExecuteAndReturnResultOrShowError<SelectOption[]>(
      thunkAPI,
      getExitAdjustmentLeas({
        signal: thunkAPI.signal as AbortSignal,
      })
    );
  }
);

export const fetchExitAdjustmentSchoolsByLeaId = createAsyncThunk(
  'exitAdjustment/fetchExitAdjustmentSchoolsByLeaId',
  async (leaId: string, thunkAPI) => {
    return await thunkExecuteAndReturnResultOrShowError<SelectOption[]>(
      thunkAPI,
      getExitAdjustmentSchoolsByLea(leaId, {
        signal: thunkAPI.signal as AbortSignal,
      })
    );
  }
);

export const fetchExitAdjustmentById = createAsyncThunk(
  'exitAdjustment/fetchExitAdjustmentById',
  async (id: string, thunkAPI) => {
    return await thunkExecuteAndReturnResultOrShowError<ExitAdjustmentModel>(
      thunkAPI,
      getExitAdjustmentById(id, {
        signal: thunkAPI.signal as AbortSignal,
      })
    );
  }
);

export const deleteExitAdjustmentThunk = createAsyncThunk(
  'exitAdjustment/delete',
  async (id: string, thunkAPI): Promise<void> => {
    return await thunkExecuteAndReturnResultOrShowError<void>(
      thunkAPI,
      deleteExitAdjustment(id, {
        signal: thunkAPI.signal as AbortSignal,
      })
    );
  }
);

export const fetchStudentByMSISId = createAsyncThunk(
  'exitAdjustment/fetchStudentByMSISId',
  async (
    params: { msisId: string; schoolYear: string },
    thunkAPI
  ) => {
    try {
      const response = await getStudentByMSISId(
        params.msisId,
        params.schoolYear,
        { signal: thunkAPI.signal as AbortSignal }
      );
      if (response.status !== 200) {
        throw new Error('NOT_FOUND');
      }
      return response.data;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('NOT_FOUND');
    }
  }
);

export const uploadExitAdjustmentFileThunk = createAsyncThunk(
  'exitAdjustment/uploadFile',
  async (
    params: {
      schoolYear: string; studentIdentifier: string;
      leaIdentifier: string; associatedRecordId: string; file: File;
    },
    thunkAPI
  ): Promise<{ id: string }> => {
    const response = await uploadExitAdjustmentFile(
      params.schoolYear,
      params.studentIdentifier,
      params.leaIdentifier,
      params.associatedRecordId,
      params.file,
      {
        signal: thunkAPI.signal as AbortSignal,
      }
    );

    if (!isValidCode(response.status)) {
      const errorMessage =
        response.error?.detail ||
        response.error?.title ||
        'Failed to upload file. Please try again.';
      throw new Error(errorMessage);
    }

    return response.data;
  }
);

export const deleteExitAdjustmentFileThunk = createAsyncThunk(
  'exitAdjustment/deleteFile',
  async (params: { leaId: string; id: string }, thunkAPI): Promise<void> => {
    return await thunkExecuteAndReturnResultOrShowError<void>(
      thunkAPI,
      deleteExitAdjustmentFile(params.leaId, params.id, {
        signal: thunkAPI.signal as AbortSignal,
      })
    );
  }
);

export const fetchAllExitAdjustmentFilesThunk = createAsyncThunk(
  'exitAdjustment/fetchAllFiles',
  async (
    leaId: string,
    thunkAPI
  ): Promise<ExitAdjustmentFileListItemModel[]> => {
    return await thunkExecuteAndReturnResultOrShowError<
      ExitAdjustmentFileListItemModel[]
    >(
      thunkAPI,
      getAllExitAdjustmentFiles(leaId, {
        signal: thunkAPI.signal as AbortSignal,
      })
    );
  }
);

export const downloadExitAdjustmentFileThunk = createAsyncThunk(
  'exitAdjustment/downloadFile',
  async (
    params: { leaId: string; metaId: string; fileName: string },
    thunkAPI
  ): Promise<void> => {
    try {
      const blob = await downloadExitAdjustmentFile(
        params.leaId,
        params.metaId,
        {
          signal: thunkAPI.signal as AbortSignal,
        }
      );

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = params.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      throw error;
    }
  }
);

export const fetchExitAdjustmentFilesByStudentThunk = createAsyncThunk(
  'exitAdjustment/fetchFilesByStudent',
  async (
    params: {
      schoolYear: string;
      leaId: string;
      msisId: string;
      showLoading?: boolean;
    },
    thunkAPI
  ): Promise<ExitAdjustmentFileListItemModel[]> => {
    return await thunkExecuteAndReturnResultOrShowError<
      ExitAdjustmentFileListItemModel[]
    >(
      thunkAPI,
      getExitAdjustmentFilesByStudent(
        params.schoolYear,
        params.leaId,
        params.msisId,
        {
          signal: thunkAPI.signal as AbortSignal,
        }
      )
    );
  }
);

export const fetchExitAdjustmentFilesByRequestThunk = createAsyncThunk(
  'exitAdjustment/fetchFilesByRequest',
  async (
    params: {
      leaId: string;
      associatedRecordId: string;
      showLoading?: boolean;
    },
    thunkAPI
  ): Promise<ExitAdjustmentFileListItemModel[]> => {
    return await thunkExecuteAndReturnResultOrShowError<
      ExitAdjustmentFileListItemModel[]
    >(
      thunkAPI,
      getExitAdjustmentFilesByRequest(params.leaId, params.associatedRecordId, {
        signal: thunkAPI.signal as AbortSignal,
      })
    );
  }
);
